import { ProgramEvent } from '@cue/api';
import { Button, Icon } from '@cue/atoms';
import { Mdx, mustache } from '@cue/utility';
import { LocalStorageUtil } from '@cue/utility';
import { useEventDataProvider } from '@project/hooks/use-event-data';
import { useEvents } from '@project/hooks/use-events';
import {
  LivestreamStatus,
  LivestreamStatusDefaultState,
  LivestreamStatusType,
  useLivestreamStatus,
} from '@project/hooks/use-livestream-status';
import { useUser } from '@project/hooks/use-user';
import { useUserSubscriptions } from '@project/hooks/use-user-subscriptions';
import { livestreams_modules } from '@project/local/livestream.json';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const alias = livestreams_modules.map((lm) => ({
  id: lm.id,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  channel: lm.livestream_channel.categories[0].categories_id?.slug,
}));

export const UserStreamNotification = (props: UserStreamNotificationProps) => {
  const LIVESTREAM_NOTIFICATION_DISMISS = 'livestreamNotificationDismiss';
  const { excludeChannels, channels } = props;

  const { user, isAuthenticated } = useUser();
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const [currentChannelState, setCurrentChannelState] = useState<LivestreamStatus | undefined>();
  const { status } = useLivestreamStatus() || LivestreamStatusDefaultState;
  const { events, isLoading } = useEvents();
  const { bookmarks, toggleBookmark } = useUserSubscriptions();
  const [notificationBookmarks, setNotificationBookmarks] = useState<string[]>([]);
  const { currentEvent } = useEventDataProvider();

  const [dismissState, setDismissState] = useState(
    (LocalStorageUtil.get<string[]>(LIVESTREAM_NOTIFICATION_DISMISS) || []) as string[],
  );

  useEffect(() => {
    setCurrentChannelState(status?.find(({ channel }) => excludeChannels?.includes(channel)));
  }, [status, excludeChannels]);

  const activeSessions = useMemo(() => {
    const liveStatus =
      status
        .filter(
          ({ channel, event, status }) =>
            event &&
            !excludeChannels?.includes(channel) &&
            status === LivestreamStatusType.ACTIVE &&
            (notificationBookmarks.includes(event) || bookmarks?.data?.includes(event)) &&
            !dismissState.includes(event),
        )
        ?.map(({ event, channel }) => ({
          event,
          channel,
          streamId: alias.find((a) => a.channel === channel)?.id,
        })) || [];
    if (liveStatus.length > 0) return liveStatus;
    const agendaCategories = channels.filter((item) => !excludeChannels?.includes(item));
    const agendaStatus =
      currentEvent({
        categories: channels.filter((item) => !excludeChannels?.includes(item)),
        slugs: 'agenda',
      })
        ?.filter(
          ({ id }) =>
            (notificationBookmarks.includes(id) || bookmarks?.data?.includes(id)) &&
            !dismissState.includes(id),
        )
        .map(({ id, categories }) => {
          const channel = categories?.find((cat) => agendaCategories.includes(cat.slug))?.slug;
          return {
            event: id,
            channel,
            streamId: alias.find((a) => a.channel === channel)?.id,
          };
        }) || [];
    return agendaStatus;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, bookmarks, notificationBookmarks, isLoading]);

  useEffect(() => {
    if (user && (user?.language === 'de' || language === 'de')) {
      if (isAuthenticated) {
        if (bookmarks.data && !bookmarks.data.includes('B2isM7wObEx8clxj-eVk9wXWj')) {
          toggleBookmark('B2isM7wObEx8clxj-eVk9wXWj');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, bookmarks.data]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (language === 'de') {
        setNotificationBookmarks(['B2isM7wObEx8clxj-eVk9wXWj']);
      }
    }
  }, [isAuthenticated]);

  const switchChannel = (channel: string) => {
    if (document.location.pathname === `/${language}/`) {
      document.location.href = `/${language}/?channel=${channel}#/live`;
    } else {
      document.location.href = `/${language}/live?channel=${channel}#/live`;
    }
  };

  if (activeSessions.length < 1 && currentChannelState?.status !== LivestreamStatusType.ACTIVE) {
    return (
      <>
        {status
          ?.filter(
            ({ event, channel, status }) =>
              !excludeChannels?.includes(channel) &&
              status === LivestreamStatusType.ACTIVE &&
              event,
          )
          ?.map((channel) => {
            const event = events?.find(({ id }) => id === channel.event) as ProgramEvent;
            return (
              <div key={channel.channel} className="cue-project-user-stream-notification">
                <div className="cue-project-user-stream-notification-info">
                  <Icon name="information" />
                </div>
                <div className="cue-project-user-stream-notification-message">
                  <Mdx>
                    {mustache(
                      t('livestream.user-notification.message-channel'),
                      event as unknown as Record<string, unknown>,
                    ) || ''}
                  </Mdx>
                </div>
                <div className="cue-project-user-stream-notification-cta">
                  <Button styling="secondary" onClick={() => switchChannel(channel.channel)}>
                    {t('livestream.user-notification.action-channel')}
                  </Button>
                </div>
              </div>
            );
          })}
      </>
    );
  }
  return (
    <>
      {activeSessions
        ?.map((session) => ({
          events: events?.find(({ id }) => id === session.event) as ProgramEvent,
          session,
        }))
        ?.map((event_session) => {
          if (!event_session.events) return <></>;
          const { id } = event_session.events;
          const { channel } = event_session.session;
          if (!channel || channel === 'default') return <></>;
          return (
            <div key={id} className="cue-project-user-stream-notification">
              <div className="cue-project-user-stream-notification-info">
                <Icon name="info" />
              </div>
              <div className="cue-project-user-stream-notification-message">
                <Mdx>
                  {mustache(
                    t('livestream.user-notification.message'),
                    event_session.events as unknown as Record<string, unknown>,
                  ) || ''}
                </Mdx>
              </div>
              <div className="cue-project-user-stream-notification-cta">
                <Button styling="secondary" onClick={() => switchChannel(channel)}>
                  {t('livestream.user-notification.action')}
                </Button>
              </div>
              <div className="cue-project-user-stream-notification-close">
                <Button
                  styling="iconOnly"
                  icon="close"
                  onClick={() => {
                    setDismissState((prev) => {
                      LocalStorageUtil.set<string[]>(LIVESTREAM_NOTIFICATION_DISMISS, [
                        id,
                        ...prev,
                      ]);
                      return [id, ...prev];
                    });
                  }}></Button>
              </div>
            </div>
          );
        })}
    </>
  );
};

export interface UserStreamNotificationProps {
  channels: string[];
  excludeChannels?: string[];
}
